/* eslint-disable id-length */
import ExcelJS from 'exceljs';

export const toTitleCase = (str) => {
  if (/^[a-z][a-zA-Z0-9]*$/.test(str)) {
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (char) => char.toUpperCase());
  }
  return str;
};

// Helper to reliably check and convert strings to numbers
const getNumericValue = (val) => {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const cleaned = val.replace(/[\s\xa0,€£₹$]/g, '');
    if (cleaned !== '' && !isNaN(Number(cleaned))) {
      return Number(cleaned);
    }
  }
  return null;
};

export const createExcelRows = ({ data, worksheet, columns }) => {
  data.forEach((row) => {
    const { format, ...requiredRowContents } = row;
    const newRow = worksheet.addRow(requiredRowContents);

    newRow.eachCell((cell, colNumber) => {
      const columnKey = columns[colNumber - 1].key;
      const cellFormat = format ? format[columnKey] : undefined;
      if (cellFormat) {
        cell.numFmt = cellFormat;
      }
    });
  });
};

const applyBorderToRowCells = (row) => {
  row.eachCell((cell) => {
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFD9DBE3' } },
      left: { style: 'thin', color: { argb: 'FFD9DBE3' } },
      bottom: { style: 'thin', color: { argb: 'FFD9DBE3' } },
      right: { style: 'thin', color: { argb: 'FFD9DBE3' } },
    };
  });
};

const applyOuterBorder = ({ worksheet, startRow, endRow, startColumn, endColumn }) => {
  const borderSide = { style: 'thin', color: { argb: 'FFD9DBE3' } };

  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
    const row = worksheet.getRow(rowIndex);

    for (let columnIndex = startColumn; columnIndex <= endColumn; columnIndex += 1) {
      const cell = row.getCell(columnIndex);
      const nextBorder = { ...(cell.border || {}) };

      if (rowIndex === startRow) nextBorder.top = borderSide;
      if (rowIndex === endRow) nextBorder.bottom = borderSide;
      if (columnIndex === startColumn) nextBorder.left = borderSide;
      if (columnIndex === endColumn) nextBorder.right = borderSide;

      cell.border = nextBorder;
    }
  }
};

const exportToExcel = async ({
  data,
  workBookTitle,
  workSheetTitle,
  filterKeyList = [],
  columnsConfig = null,
  prependRows = [],
  borderAllCells = false,
  outerBorder = false,
  hasTotalRow = false,
  totalCellAlignment = 'right',
}) => {
  const tableData = data || [];
  const filterKeys = ['format', ...filterKeyList];
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Darpan 2';
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.lastPrinted = new Date();
  workbook.title = workBookTitle;
  const worksheet = workbook.addWorksheet(workSheetTitle);

  const columns = (Array.isArray(columnsConfig) && columnsConfig.length > 0)
    ? columnsConfig
    : Object.keys(tableData[0] || {})
      .filter((key) => !filterKeys.includes(key))
      .map((key) => ({
        header: toTitleCase(key),
        key: key,
        width: 30,
      }));
  worksheet.columns = columns;

  if (prependRows.length) {
    worksheet.spliceRows(
      1,
      0,
      ...prependRows.map((rowConfig) => (
        Array.isArray(rowConfig?.values) ? rowConfig.values : ['']
      )),
    );

    prependRows.forEach((rowConfig, index) => {
      const prependedRow = worksheet.getRow(index + 1);
      if (rowConfig?.font) prependedRow.font = rowConfig.font;
      if (rowConfig?.alignment) {
        prependedRow.eachCell((cell) => {
          cell.alignment = rowConfig.alignment;
        });
      }
      if (rowConfig?.mergeAcross) {
        worksheet.mergeCells(
          prependedRow.number,
          1,
          prependedRow.number,
          Math.max(1, Number(rowConfig.mergeAcross)),
        );
      }
    });
  }

  const headerRow = worksheet.getRow(prependRows.length + 1);
  headerRow.font = { bold: true };
  headerRow.height = 40;

  createExcelRows({ data: tableData, columns, worksheet });

  const lastRowNumber = prependRows.length + 1 + tableData.length;

  // POST-PROCESSING: Hard-lock alignments and format summary rows safely
  columns.forEach((colConfig, colIndex) => {
    const colNumber = colIndex + 1;
    const rawKey = colConfig.field || colConfig.key || colConfig.header || '';
    const columnKey = colConfig.key || colConfig.field || rawKey;
    const keyClean = String(rawKey)
      .toLowerCase()
      .replace(/[\s_]/g, '');

    const isIdColumn
      = keyClean === 'projectid'
      || keyClean === 'empid'
      || keyClean === 'employeeid'
      || keyClean === 'id';

    const isExplicitlyRightAligned
      = colConfig.align === 'right'
      || colConfig.alignment === 'right'
      || colConfig.headerAlign === 'right'
      || colConfig.headerAlignment === 'right'
      || colConfig.alignment?.horizontal === 'right'
      || colConfig.headerAlignment?.horizontal === 'right'
      || colConfig.type === 'number'
      || isIdColumn;

    let numberCount = 0;
    let textCount = 0;
    let hasDecimals = false;

    for (let i = 0; i < tableData.length; i += 1) {
      const val = tableData[i][columnKey];
      if (val !== null && val !== undefined && val !== '') {
        const numVal = getNumericValue(val);
        if (!isIdColumn && numVal !== null) {
          numberCount += 1;
          if (String(val).includes('.')) hasDecimals = true;
        } else {
          textCount += 1;
        }
      }
    }

    const isColumnPredominantlyNumeric = isExplicitlyRightAligned || (numberCount > 0 && numberCount >= textCount);

    worksheet.getColumn(colNumber).eachCell({ includeEmpty: false }, (cell, rowNumber) => {
      if (rowNumber <= prependRows.length) return;

      const isHeader = rowNumber === prependRows.length + 1;
      const isLastRow = rowNumber === lastRowNumber;

      let isCellNumeric = false;

      if (!isHeader && keyClean === 'projectid') {
        const numVal = getNumericValue(cell.value);
        if (numVal !== null) {
          isCellNumeric = true;
          cell.value = numVal;
          cell.numFmt = '0';
        }
      } else if (!isHeader && !isIdColumn) {
        const numVal = getNumericValue(cell.value);
        if (numVal !== null) {
          isCellNumeric = true;
          cell.value = numVal;

          if (hasDecimals && !cell.numFmt) {
            cell.numFmt = '0.00';
          }
        }
      } else if (!isHeader && isIdColumn) {
        if (cell.value !== null && cell.value !== undefined) {
          cell.value = String(cell.value).replace(/,/g, '');
        }
      }

      const shouldAlignRight = isColumnPredominantlyNumeric || isCellNumeric || isExplicitlyRightAligned;

      // STRICT CHECK: ONLY apply Total formatting if hasTotal === true AND it is the last row
      if (hasTotalRow && isLastRow) {
        const cellStringText = String(cell.value || '').trim()
          .toLowerCase();

        if (['total', 'total:', 'grand total', 'grand total:'].includes(cellStringText)) {
          cell.font = { bold: true };
        } else if (isCellNumeric || isExplicitlyRightAligned) {
          cell.font = { bold: true };
        }
      }

      const alignToUse = shouldAlignRight ? totalCellAlignment : 'left';
      cell.alignment = {
        horizontal: alignToUse,
        wrapText: true,
      };
    });
  });

  if (borderAllCells) {
    worksheet.eachRow((row) => {
      applyBorderToRowCells(row);
    });
  }
  if (outerBorder && columns.length) {
    applyOuterBorder({
      worksheet,
      startRow: 1,
      endRow: prependRows.length + tableData.length + 1,
      startColumn: 1,
      endColumn: columns.length,
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${workBookTitle}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default exportToExcel;
